import React, { useEffect, useState } from "react";
import { Card, Col, Row, Button, Form, Spinner, Table, InputGroup, Modal, Alert } from "react-bootstrap";
import { db, auth } from "../firebaseConfig";
import { collection, getDocs, updateDoc, doc, getDoc } from "firebase/firestore";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function ManageAllLeaves() {
  const [allLeaveRequests, setAllLeaveRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchDate, setSearchDate] = useState("");
  const [searchUser, setSearchUser] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [detailsModalShow, setDetailsModalShow] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [rolePermissions, setRolePermissions] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(db, "Users", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userRole = userDoc.data().role || "";
          setCurrentUserRole(userRole);

          if (userRole === "admin") {
            setRolePermissions({ canApproveDenyLeaves: true });
          } else {
            const roleDocRef = doc(db, "Roles", userRole);
            const roleDoc = await getDoc(roleDocRef);

            if (roleDoc.exists()) {
              setRolePermissions(roleDoc.data().permissions);
            } else {
              setRolePermissions(null);
            }
          }
        }
      }
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    if (rolePermissions && !rolePermissions.canApproveDenyLeaves && currentUserRole !== "admin") {
      setAccessDenied(true);
    }
  }, [rolePermissions]);

  useEffect(() => {
    const fetchAllLeaveRequests = async () => {
      setLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, "leave_requests"));
        const leaves = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllLeaveRequests(leaves);
        setFilteredRequests(leaves);

        const uniqueUsers = [...new Set(leaves.map((leave) => leave.fullName))];
        setUserOptions(uniqueUsers);
      } catch (error) {
        console.error("Error fetching leave requests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllLeaveRequests();
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    setLoading(true);
    try {
      const leaveDocRef = doc(db, "leave_requests", id);
      await updateDoc(leaveDocRef, { status: newStatus });

      const updatedRequests = allLeaveRequests.map((leave) =>
        leave.id === id ? { ...leave, status: newStatus } : leave
      );
      setAllLeaveRequests(updatedRequests);
      setFilteredRequests(updatedRequests);
      setDetailsModalShow(false);
    } catch (error) {
      console.error("Error updating leave status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    let filtered = allLeaveRequests;

    if (searchDate) {
      filtered = filtered.filter(
        (leave) =>
          new Date(leave.startDate).toISOString().slice(0, 10) <= searchDate &&
          new Date(leave.endDate).toISOString().slice(0, 10) >= searchDate
      );
    }

    if (searchUser) {
      filtered = filtered.filter((leave) =>
        leave.fullName.toLowerCase().includes(searchUser.toLowerCase())
      );
    }

    setFilteredRequests(filtered);
  };

  const clearFilters = () => {
    setSearchDate("");
    setSearchUser("");
    setFilteredRequests(allLeaveRequests);
  };

  const handleRowClick = (leave) => {
    setSelectedLeave(leave);
    setDetailsModalShow(true);
  };

  if (accessDenied) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <h1 className="text-center text-danger">403 Forbidden</h1>
          <p className="text-center">You do not have permission to access this page.</p>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-leaf-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">Manage All Leaves</h1>
          </div>
          <p className="lead text-muted">Review and manage employee leave requests</p>
        </div>

        {loading ? (
          <div className="text-center my-5">
            <div className="d-flex flex-column align-items-center">
              <Spinner animation="border" variant="primary" size="lg" />
              <p className="mt-3 text-muted">Loading leave requests...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Enhanced Search Filters */}
            <Card className="border-0 shadow-lg rounded-4 mb-5">
              <div className="bg-gradient-primary text-white p-4 rounded-top-4" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-search-line fs-4 me-3"></i>
                  <h5 className="mb-0 fw-bold">Search & Filter</h5>
                </div>
              </div>
              <Card.Body className="p-4">
                <Row className="g-4">
                  <Col md="6" lg="4">
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        <i className="ri-calendar-line me-2"></i>Search by Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={searchDate}
                        onChange={(e) => setSearchDate(e.target.value)}
                        className="form-control-lg shadow-sm border-2"
                        style={{ borderRadius: '12px' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md="6" lg="4">
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        <i className="ri-user-search-line me-2"></i>Search by Username
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          list="userOptions"
                          placeholder="Type to search"
                          value={searchUser}
                          onChange={(e) => setSearchUser(e.target.value)}
                          className="form-control-lg shadow-sm border-2"
                          style={{ borderRadius: '12px' }}
                        />
                        <datalist id="userOptions">
                          {userOptions.map((user, index) => (
                            <option key={index} value={user} />
                          ))}
                        </datalist>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md="6" lg="4" className="d-flex align-items-end">
                    <div className="d-flex gap-2 w-100">
                      <Button 
                        variant="primary" 
                        onClick={handleSearch}
                        className="flex-fill py-3 rounded-pill fw-semibold"
                      >
                        <i className="ri-search-line me-2"></i>
                        Search
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        onClick={clearFilters}
                        className="flex-fill py-3 rounded-pill fw-semibold"
                      >
                        <i className="ri-refresh-line me-2"></i>
                        Clear
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Enhanced Leave Requests Table */}
            <Card className="border-0 shadow-lg rounded-4">
              <div className="bg-gradient-info text-white p-4 rounded-top-4" 
                   style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="ri-file-list-3-line fs-4 me-3"></i>
                    <h5 className="mb-0 fw-bold">All Leave Requests</h5>
                  </div>
                  <span className="badge bg-white text-info px-3 py-2 rounded-pill">
                    {filteredRequests.length} requests
                  </span>
                </div>
              </div>
              <Card.Body className="p-0">
                {filteredRequests.length > 0 ? (
                  <div className="table-responsive">
                    <Table responsive hover className="mb-0">
                      <thead className="table-dark">
                        <tr>
                          <th className="border-0">
                            <i className="ri-user-line me-2"></i>Username
                          </th>
                          <th className="border-0">
                            <i className="ri-mail-line me-2"></i>Email
                          </th>
                          <th className="border-0">
                            <i className="ri-calendar-event-line me-2"></i>Start Date
                          </th>
                          <th className="border-0">
                            <i className="ri-calendar-check-line me-2"></i>End Date
                          </th>
                          <th className="border-0">
                            <i className="ri-file-text-line me-2"></i>Reason
                          </th>
                          <th className="border-0">
                            <i className="ri-flag-line me-2"></i>Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRequests.map((leave, index) => (
                          <tr
                            key={index}
                            onClick={() => handleRowClick(leave)}
                            style={{ 
                              cursor: "pointer",
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                              e.currentTarget.style.transform = 'scale(1.01)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <td className="fw-semibold">{leave.fullName}</td>
                            <td className="text-muted">{leave.email}</td>
                            <td className="fw-semibold">{leave.startDate}</td>
                            <td className="fw-semibold">{leave.endDate}</td>
                            <td className="text-truncate" style={{ maxWidth: '200px' }} title={leave.reason}>
                              {leave.reason}
                            </td>
                            <td>
                              <span
                                className={`badge rounded-pill px-3 py-2 fw-semibold ${
                                  leave.status === "Approved"
                                    ? "bg-success"
                                    : leave.status === "Declined"
                                    ? "bg-danger"
                                    : "bg-warning text-dark"
                                }`}
                              >
                                {leave.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <i className="ri-file-list-3-line text-muted" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h5 className="text-muted mb-3">No Leave Requests Found</h5>
                    <p className="text-muted">No leave requests match your current search criteria.</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}

        <Footer />

        {/* Enhanced Leave Details Modal */}
        {selectedLeave && (
          <Modal
            show={detailsModalShow}
            onHide={() => setDetailsModalShow(false)}
            centered
            size="lg"
          >
            <Modal.Header closeButton className="bg-gradient-primary text-white border-0">
              <Modal.Title className="fw-bold">
                <i className="ri-file-text-line me-2"></i>
                Leave Request Details
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              <Row className="g-4">
                <Col md={6}>
                  <Card className="border-0 shadow-sm rounded-3 h-100">
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                          <i className="ri-user-line fs-4 text-primary"></i>
                        </div>
                        <div>
                          <h6 className="fw-bold mb-0">Employee Information</h6>
                          <small className="text-muted">Personal details</small>
                        </div>
                      </div>
                      <div className="mb-3">
                        <strong className="text-primary">Username:</strong>
                        <p className="mb-0 mt-1">{selectedLeave.fullName}</p>
                      </div>
                      <div>
                        <strong className="text-primary">Email:</strong>
                        <p className="mb-0 mt-1">{selectedLeave.email}</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-0 shadow-sm rounded-3 h-100">
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
                          <i className="ri-calendar-line fs-4 text-info"></i>
                        </div>
                        <div>
                          <h6 className="fw-bold mb-0">Leave Period</h6>
                          <small className="text-muted">Date range</small>
                        </div>
                      </div>
                      <div className="mb-3">
                        <strong className="text-info">Start Date:</strong>
                        <p className="mb-0 mt-1">{selectedLeave.startDate}</p>
                      </div>
                      <div>
                        <strong className="text-info">End Date:</strong>
                        <p className="mb-0 mt-1">{selectedLeave.endDate}</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={12}>
                  <Card className="border-0 shadow-sm rounded-3">
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                          <i className="ri-file-text-line fs-4 text-warning"></i>
                        </div>
                        <div>
                          <h6 className="fw-bold mb-0">Leave Details</h6>
                          <small className="text-muted">Reason and status</small>
                        </div>
                      </div>
                      <div className="mb-3">
                        <strong className="text-warning">Reason:</strong>
                        <p className="mb-0 mt-1">{selectedLeave.reason}</p>
                      </div>
                      <div>
                        <strong className="text-warning">Status:</strong>
                        <div className="mt-1">
                          <span
                            className={`badge rounded-pill px-3 py-2 fw-semibold ${
                              selectedLeave.status === "Approved"
                                ? "bg-success"
                                : selectedLeave.status === "Declined"
                                ? "bg-danger"
                                : "bg-warning text-dark"
                            }`}
                          >
                            {selectedLeave.status}
                          </span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="border-0 p-4">
              <div className="d-flex gap-3 w-100">
                <Button
                  variant="success"
                  onClick={() => handleStatusChange(selectedLeave.id, "Approved")}
                  className="flex-fill py-3 rounded-pill fw-semibold"
                >
                  <i className="ri-check-line me-2"></i>
                  Approve Leave
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleStatusChange(selectedLeave.id, "Declined")}
                  className="flex-fill py-3 rounded-pill fw-semibold"
                >
                  <i className="ri-close-line me-2"></i>
                  Decline Leave
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    </React.Fragment>
  );
}
